import api from './api'
import { parseParams, parseUrlFilters } from './common'
import filterUtils from '@/mixins/filterUtils'
import { CHANNEL_OPTIONS, CHANNEL_OPTIONS_EXTERIOR, CAMPAIGN_STATUSES } from '@/utils/campaignOptions'
import { FILTER_MAPPING } from './filterEndpointMapping'
import authService from '@/api/authService'

const ACTIVATION_HOURS = {
  dayparting_mon: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
  dayparting_tue: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
  dayparting_wed: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
  dayparting_thu: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
  dayparting_fri: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
  dayparting_sat: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
  dayparting_sun: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0]
}

export const TASK_STATUSES = {
  RUNNING: 'RUNNING',
  FAILED: 'FAILED',
  COMPLETED: 'COMPLETED'
}

export default {
  getCampaigns (params, filters = [], extras) {
    const urlParams = parseParams(params)
    let urlFilters = this.parseCampaignFilters(filters)
    if (extras) urlFilters = `${urlFilters}&${extras}`
    return api.get(`/campaigns?${urlParams}&${urlFilters}`).then(res => res.data)
  },
  getCampaignsCSV (params, filters = [], extras) {
    const urlParams = parseParams(params)
    let urlFilters = this.parseCampaignFilters(filters)
    if (extras) urlFilters = `${urlFilters}&${extras}`
    const link = document.createElement('a')
    link.href = `${process.env.VUE_APP_API_URL}/dashboard/channels/stats/extended/campaignchannels?${urlParams}&${urlFilters}`
    // Since download uses cookies instead of bearer header, ensure the token has
    // been refreshed to have the cookie set
    authService.refreshAuth().then(() => {
      link.click()
    })
  },
  getCampaignsTemplates (params) {
    const urlParams = parseParams(params)
    return api.get(`/campaigns/templates?${urlParams}`).then(res => res.data)
  },
  parseCampaignFilters (filters) {
    const mapping = FILTER_MAPPING['campaigns']
    if (!mapping) {
      return ''
    }
    const params = filters
      .filter(f => (mapping[f.key] && filterUtils.methods.filterHasValue(f)))
      .reduce((acum, f) => {
        const key = mapping[f.key]
        acum[key] = f.value instanceof Date
          ? f.value.toISOString().split('T')[0]
          : String(f.value)
        return acum
      }, {})

    const specificParams = filters
      .filter(f => f.key === 'campaign_channel' || f.key === 'campaign_channel_summary')
      .reduce((acum, f) => {
        Object.keys({ ...CHANNEL_OPTIONS, ...CHANNEL_OPTIONS_EXTERIOR }).forEach(type => {
          const key = `${type.toLowerCase()}_channel__isnull`
          if ((f.value || []).indexOf(type) !== -1) {
            acum[key] = 'false'
          }
        })
        return acum
      }, {})

    return parseParams({ ...params, ...specificParams })
  },
  getCampaign ({ id, ...params }) {
    return api.get(`/campaigns/${id}?${parseParams(params)}`).then(res => res.data)
  },
  getCampaignComparison ({ ids, ...params }) {
    return Promise.all(ids.map(id => {
      return api.get(`/campaigns/${id}?${parseParams(params)}`).then(res => res.data)
    }))
  },
  getSummaryCSV (filters = []) {
    const urlFilters = this.parseCampaignFilters(filters)
    return api.get(`/campaigns/summary?${urlFilters}`, { responseType: 'arraybuffer' }).then(res => res.data)
  },
  getBillingSummaryCSV (params, filters) {
    const urlParams = parseParams(params)
    const urlFilters = parseUrlFilters('billing', filters)
    return api.get(`/campaigns/billing/summary?${urlParams}&${urlFilters}`, { responseType: 'arraybuffer' }).then(res => res.data)
  },
  createCampaign (payload) {
    return api.post(`/campaigns`, payload).then(res => res.data)
  },
  updateCampaign ({ id, ...payload }) {
    return api.put(`/campaigns/${id}`, payload).then(res => res.data)
  },
  deleteCampaign ({ id }) {
    return api.delete(`/campaigns/${id}`).then(res => res.data)
  },
  updateStatus ({ id, ...params }) {
    return api.put(`/campaigns/${id}/status`, params).then(res => res.data)
  },
  activate ({ id, ...params }) {
    return api.put(`/campaigns/${id}/activate`, ACTIVATION_HOURS).then(res => res.data)
  },
  getBilling (params, filters) {
    const urlParams = parseParams(params)
    const urlFilters = parseUrlFilters('billing', filters)
    return api.get(`/campaigns/billing?${urlParams}&${urlFilters}`).then(res => res.data)
      .then(data => data.map(d => {
        d.metrics = d.metrics[0] ? d.metrics[0].data : []
        return d
      }))
  },
  regenerateBilling ({ id }) {
    return api.put(`/campaigns/billing/regenerate/${id}`).then(res => res.data)
  },
  getBillingRegenerationState () {
    return api.get(`/campaigns/billing/regenerate/state`).then(res => res.data)
  },
  getBillingSummaryState (params, filters) {
    const urlParams = parseParams(params)
    const urlFilters = parseUrlFilters('billing', filters)
    return api.get(`/campaigns/billing/summary/state?${urlParams}&${urlFilters}`).then(res => res.data)
  },
  getBillingTotal (filters, type) {
    if (type) filters = filters.filter(f => f.key !== 'campaign_channel_summary')
    let urlFilters = parseUrlFilters('dashboard_summary', filters)
    if (type) urlFilters = `${urlFilters}&campaign_channel=${type}`
    return api.get(`/campaigns/billing/totals?${urlFilters}`).then(res => res.data)
      .then(data => data.total)
  },
  getPlannedBillingTotal (filters, extras) {
    let urlFilters = parseUrlFilters('dashboard_summary', filters)
    if (extras) urlFilters = `${urlFilters}&${extras}`
    return api.get(`/campaigns/billing/planned?${urlFilters}&status=ACTIVE,FINISHED`).then(res => res.data)
      .then(data => data.total)
  },
  closeBilling (shopId) {
    return api.post(`/campaigns/billing/${shopId}/close`).then(res => res.data)
  },
  updateBilling ({ id, available, po, metrics, editingPO }) {
    metrics = [{ data: metrics }]
    if (!editingPO) return api.put(`/campaigns/billing/${id}`, { available, metrics }).then(res => res.data)
    return api.put(`/campaigns/billing/${id}`, { po }).then(res => res.data)
  },
  copyCampaign ({ id, apply, shopPrefix, body }) {
    const params = parseParams({
      apply: apply || undefined,
      shop_prefix: shopPrefix || undefined
    })
    return api.post(`/campaigns/${id}/copy?${params}`, body).then(res => res.data)
  },
  getExteriorProvisioning ({ id, ...params }) {
    const urlParams = parseParams(params)
    return api.get(`/campaigns/${id}/provisioning?${urlParams}`).then(res => res.data)
  },
  getExteriorProvisioningDetails (campaignId, blockId) {
    return api.get(`/campaigns/${campaignId}/provisioning/${blockId}`).then(res => res.data)
  },
  async saveExteriorProvisioningDetails (campaignId, blockIds, file) {
    for (const id of blockIds) {
      const url = `/campaigns/${campaignId}/provisioning/${id}`
      const formData = new FormData()
      formData.append('file', file)

      await api.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  },
  getExteriorCampaignsForCalendar ({ shops, startDate, finishDate }) {
    const params = parseParams({
      campaign__shops__id__in: shops,
      start_date__gte: startDate,
      finish_date__lte: finishDate,
      campaign__status: CAMPAIGN_STATUSES.AGENCY_CONFIRMED,
      type: 'EXTERIOR',
      ordering: '-start_date',
      page_size: 10000
    })
    return api.get(`/campaigns/channels?${params}`).then(res => res.data)
  },
  getCTAs (params = {}) {
    const urlParams = parseParams(params)
    return api.get(`/campaigns/ctas?${urlParams}`).then(res => res.data.results)
  },
  /* Getting the availability of the exterior campaigns for the calendar. */
  getExteriorCampaignsAvailabilityForCalendar ({ shops, startDate, finishDate }) {
    const params = parseParams({
      finish_date: finishDate,
      start_date: startDate,
      shop_related__in: shops,
      availability: true,
      advert_type: 'EXTERIOR',
      date_info: true
    })
    return api.get(`/adverts/availability?${params}`).then(res => res.data)
  },
  getCampaignsTasks (params = {}) {
    return api.get(`/campaigns/copyingtasks/?${parseParams(params)}`).then(res => res.data)
  },
  deleteCampaignTasks () {
    return api.delete('/campaigns/copyingtasks/')
  }
}
